import React, { forwardRef, startTransition, useEffect, useImperativeHandle, useState } from "react";
import { Accordion, Alert, Badge, Button, Divider, Group, Modal, Progress, Stack, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { fromMantineDate } from "../helpers/mantineDates";
import { IconCheck, IconFileDescription, IconPrinter, IconX } from "@tabler/icons-react";
import { Timestamp, collection, doc, limit, onSnapshot, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useFirestore, useFunctions } from "../helpers/firebaseContext";
import { BaseResult, Portfolio } from "../types";

type QueueGenevaReportRequest = {
    reportName: string;
    reportTitle: string;
    customerID?: string;
    portfolioId?: string;
    parameters: Record<string, string>;
};

type QueueGenevaReportResponse = {
    reportId?: string;
    reportPath?: string;
    customerReportPath?: string | null;
    customerId?: string | null;
    customerResolutionSource?: string | null;
};

type GenevaReportDoc = {
    reportName?: string;
    reportTitle?: string | null;
    parameters?: Record<string, string>;
    createdAt?: Timestamp | null;
    completedAt?: Timestamp | null;
    status?: "queued" | "processing" | "completed" | "error";
    stage?: string | null;
    message?: string | null;
    progressPercent?: number | null;
    genevaRequestId?: string | null;
    result?: {
        rowCount?: number;
        columns?: string[];
    };
    error?: {
        message?: string;
    };
};

type GenevaReportHistoryItem = GenevaReportDoc & {
    id: string;
};

export type GenevaReportFieldValue = Date | string | null;
export type GenevaReportFieldValues = Record<string, GenevaReportFieldValue>;

type GenevaReportContextBase = {
    portfolio: Portfolio;
    portfolioIdentifier: string;
    customerId?: string;
};

type GenevaReportContext = GenevaReportContextBase & {
    values: GenevaReportFieldValues;
};

type GenevaReportFieldDefinitionBase = {
    key: string;
    label: string;
    description?: string;
    required?: boolean;
    defaultValue?: GenevaReportFieldValue | ((context: GenevaReportContextBase) => GenevaReportFieldValue);
};

type GenevaReportDateFieldDefinition = GenevaReportFieldDefinitionBase & {
    type: "date";
    maxDate?: Date | "today" | ((context: GenevaReportContext) => Date | undefined);
    minDate?: Date | "today" | ((context: GenevaReportContext) => Date | undefined);
    valueFormat?: string;
};

type GenevaReportTextFieldDefinition = GenevaReportFieldDefinitionBase & {
    type: "text";
    placeholder?: string;
};

export type GenevaReportFieldDefinition = GenevaReportDateFieldDefinition | GenevaReportTextFieldDefinition;
type GenevaReportTriggerProps = {
    openModal: () => void;
    disabled: boolean;
};

export type GenevaReportLauncherHandle = {
    openModal: () => void;
};

interface GenevaReportLauncherButtonProps {
    portfolio: Portfolio;
    customerId?: string;
    reportName: string;
    reportTitle: string;
    buttonLabel?: string;
    description?: string | ((context: GenevaReportContextBase) => string);
    fields?: GenevaReportFieldDefinition[];
    buildParameters: (context: GenevaReportContext) => Record<string, string>;
    validate?: (context: GenevaReportContext) => string | undefined;
    renderTrigger?: (props: GenevaReportTriggerProps) => React.ReactNode;
}

const getStatusColor = (status?: GenevaReportDoc["status"]): string => {
    switch (status) {
        case "completed":
            return "teal";
        case "error":
            return "red";
        case "processing":
            return "blue";
        case "queued":
        default:
            return "gray";
    }
};

const getTimestampMillis = (value?: Timestamp | null): number => {
    return value instanceof Timestamp ? value.toMillis() : 0;
};

const formatReportRunDate = (value?: Timestamp | null): string => {
    if (!(value instanceof Timestamp)) {
        return "Pending timestamp";
    }

    return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(value.toDate());
};

const resolveFieldDefaultValue = (
    field: GenevaReportFieldDefinition,
    context: GenevaReportContextBase,
): GenevaReportFieldValue => {
    if (typeof field.defaultValue === "function") {
        return field.defaultValue(context);
    }

    if (field.defaultValue !== undefined) {
        return field.defaultValue;
    }

    return field.type === "text" ? "" : null;
};

const buildInitialFieldValues = (
    fields: GenevaReportFieldDefinition[],
    context: GenevaReportContextBase,
): GenevaReportFieldValues => {
    return fields.reduce<GenevaReportFieldValues>((accumulator, field) => {
        accumulator[field.key] = resolveFieldDefaultValue(field, context);
        return accumulator;
    }, {});
};

const resolveDateConstraint = (
    constraint: GenevaReportDateFieldDefinition["minDate"] | GenevaReportDateFieldDefinition["maxDate"],
    context: GenevaReportContext,
): Date | undefined => {
    if (constraint === "today") {
        return new Date();
    }

    if (typeof constraint === "function") {
        return constraint(context);
    }

    return constraint;
};

const isMissingFieldValue = (value: GenevaReportFieldValue): boolean => {
    if (value === null || value === undefined) {
        return true;
    }

    if (typeof value === "string") {
        return value.trim().length === 0;
    }

    return false;
};

const GenevaReportLauncherButton = forwardRef<GenevaReportLauncherHandle, GenevaReportLauncherButtonProps>(({
    portfolio,
    customerId,
    reportName,
    reportTitle,
    buttonLabel,
    description,
    fields = [],
    buildParameters,
    validate,
    renderTrigger,
}, ref) => {
    const functions = useFunctions();
    const firestore = useFirestore();
    functions.region = "europe-west1";

    const portfolioIdentifier = portfolio.code || portfolio.id || "";
    const baseContext: GenevaReportContextBase = {
        portfolio,
        portfolioIdentifier,
        customerId,
    };

    const [opened, setOpened] = useState(false);
    const [fieldValues, setFieldValues] = useState<GenevaReportFieldValues>(() => buildInitialFieldValues(fields, baseContext));
    const [submitting, setSubmitting] = useState(false);
    const [reportPath, setReportPath] = useState<string | null>(null);
    const [reportDoc, setReportDoc] = useState<GenevaReportDoc | null>(null);
    const [queuedReportId, setQueuedReportId] = useState<string | null>(null);
    const [recentReports, setRecentReports] = useState<GenevaReportHistoryItem[]>([]);

    useEffect(() => {
        if (!reportPath) {
            return undefined;
        }

        const unsubscribe = onSnapshot(doc(firestore, reportPath), (snapshot) => {
            if (!snapshot.exists()) {
                return;
            }

            startTransition(() => {
                setReportDoc(snapshot.data() as GenevaReportDoc);
            });
        });

        return unsubscribe;
    }, [firestore, reportPath]);

    useEffect(() => {
        if (!opened || !portfolioIdentifier) {
            return undefined;
        }

        const reportsCollection = customerId
            ? collection(firestore, "customers", customerId, "reports")
            : collection(firestore, "reports");

        const recentReportsQuery = query(
            reportsCollection,
            where("portfolioId", "==", portfolioIdentifier),
            limit(30),
        );

        const unsubscribe = onSnapshot(recentReportsQuery, (snapshot) => {
            const nextReports = snapshot.docs
                .map((snapshotDoc) => ({
                    id: snapshotDoc.id,
                    ...(snapshotDoc.data() as GenevaReportDoc),
                }))
                .filter((item) => item.reportName === reportName)
                .sort((left, right) => getTimestampMillis(right.createdAt) - getTimestampMillis(left.createdAt))
                .slice(0, 8);

            startTransition(() => {
                setRecentReports(nextReports);
            });
        });

        return unsubscribe;
    }, [customerId, firestore, opened, portfolioIdentifier, reportName]);

    const reportStatusColor = getStatusColor(reportDoc?.status);
    const modalDescription = typeof description === "function"
        ? description(baseContext)
        : description ?? `Queue a Geneva report for ${portfolioIdentifier}.`;
    const resolvedReportId = queuedReportId ?? reportPath?.split("/").pop() ?? null;

    const handleFieldValueChange = (fieldKey: string, value: GenevaReportFieldValue) => {
        setFieldValues((currentValues) => ({
            ...currentValues,
            [fieldKey]: value,
        }));
    };

    const openModal = () => {
        setFieldValues(buildInitialFieldValues(fields, baseContext));
        setOpened(true);
    };

    useImperativeHandle(ref, () => ({
        openModal,
    }), [openModal]);

    const handleGenerate = async () => {
        if (!portfolioIdentifier) {
            notifications.show({
                color: "red",
                title: "Missing portfolio code",
                message: "This portfolio does not have a portfolio code or ID to send to Geneva.",
                icon: <IconX size={18} />,
            });
            return;
        }

        const missingField = fields.find((field) => field.required && isMissingFieldValue(fieldValues[field.key]));
        if (missingField) {
            notifications.show({
                color: "red",
                title: "Missing parameter",
                message: `Enter a value for ${missingField.label}.`,
                icon: <IconX size={18} />,
            });
            return;
        }

        const reportContext: GenevaReportContext = {
            ...baseContext,
            values: fieldValues,
        };

        const validationError = validate?.(reportContext);
        if (validationError) {
            notifications.show({
                color: "red",
                title: "Invalid report parameters",
                message: validationError,
                icon: <IconX size={18} />,
            });
            return;
        }

        setSubmitting(true);
        setReportDoc(null);
        setReportPath(null);
        setQueuedReportId(null);

        const pendingReportWindow = window.open("", "_blank");
        if (pendingReportWindow) {
            pendingReportWindow.document.title = `Preparing ${reportTitle}`;
            pendingReportWindow.document.body.innerHTML = `
                <div style="font-family: Arial, sans-serif; padding: 24px; color: #1f1f1f;">
                    <h2 style="margin: 0 0 12px;">Preparing ${reportTitle}</h2>
                    <p style="margin: 0;">The report window will load automatically once the request is queued.</p>
                </div>
            `;
        }

        const notificationId = notifications.show({
            loading: true,
            title: `Generating ${reportTitle}`,
            message: `Queuing report for portfolio ${portfolioIdentifier}.`,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            const queueGenevaReport = httpsCallable<QueueGenevaReportRequest, BaseResult>(functions, "queueGenevaReport");
            const result = await queueGenevaReport({
                reportName,
                reportTitle,
                customerID: customerId,
                portfolioId: portfolioIdentifier,
                parameters: buildParameters(reportContext),
            });

            const response = result.data?.data as QueueGenevaReportResponse | undefined;
            setReportPath(response?.reportPath ?? null);
            setQueuedReportId(response?.reportId ?? null);

            if (pendingReportWindow && response?.reportId) {
                pendingReportWindow.location.href = `/report-print.html?reportId=${encodeURIComponent(response.reportId)}`;
            } else if (pendingReportWindow) {
                pendingReportWindow.close();
            }

            notifications.update({
                id: notificationId,
                color: "teal",
                title: `${reportTitle} queued`,
                message: response?.reportId ? `Report ${response.reportId} is now processing.` : "Report queued successfully.",
                icon: <IconCheck size={18} />,
                loading: false,
                autoClose: 4000,
            });
        } catch (error) {
            if (pendingReportWindow) {
                pendingReportWindow.close();
            }

            const message = error instanceof Error ? error.message : "Please try again later.";
            notifications.update({
                id: notificationId,
                color: "red",
                title: `Failed to queue ${reportTitle}`,
                message,
                icon: <IconX size={18} />,
                loading: false,
                autoClose: 6000,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {renderTrigger ? (
                renderTrigger({
                    openModal,
                    disabled: submitting,
                })
            ) : (
                <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconFileDescription size={16} />}
                    onClick={openModal}
                >
                    {buttonLabel ?? `Generate ${reportTitle}`}
                </Button>
            )}

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title={reportTitle}
                centered
            >
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        {modalDescription}
                    </Text>

                    {fields.map((field) => {
                        const fieldContext: GenevaReportContext = {
                            ...baseContext,
                            values: fieldValues,
                        };

                        if (field.type === "date") {
                            return (
                                <Stack key={field.key} gap={4}>
                                    <DateInput
                                        label={field.label}
                                        value={fieldValues[field.key] instanceof Date ? fieldValues[field.key] as Date : null}
                                        onChange={(value) => handleFieldValueChange(field.key, fromMantineDate(value))}
                                        valueFormat={field.valueFormat ?? "DD/MM/YYYY"}
                                        minDate={resolveDateConstraint(field.minDate, fieldContext)}
                                        maxDate={resolveDateConstraint(field.maxDate, fieldContext)}
                                        disabled={submitting}
                                        withAsterisk={field.required}
                                    />
                                    {field.description && (
                                        <Text size="xs" c="dimmed">
                                            {field.description}
                                        </Text>
                                    )}
                                </Stack>
                            );
                        }

                        return (
                            <Stack key={field.key} gap={4}>
                                <TextInput
                                    label={field.label}
                                    value={typeof fieldValues[field.key] === "string" ? fieldValues[field.key] as string : ""}
                                    onChange={(event) => handleFieldValueChange(field.key, event.currentTarget.value)}
                                    placeholder={field.placeholder}
                                    disabled={submitting}
                                    withAsterisk={field.required}
                                />
                                {field.description && (
                                    <Text size="xs" c="dimmed">
                                        {field.description}
                                    </Text>
                                )}
                            </Stack>
                        );
                    })}

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setOpened(false)} disabled={submitting}>
                            Close
                        </Button>
                        <Button onClick={handleGenerate} loading={submitting}>
                            Generate
                        </Button>
                    </Group>

                    {(queuedReportId || reportDoc) && (
                        <Alert color={reportStatusColor} title="Latest report status" variant="light">
                            <Stack gap="xs">
                                <Group gap="xs">
                                    <Badge color={reportStatusColor} variant="filled">
                                        {reportDoc?.status || "queued"}
                                    </Badge>
                                    {reportDoc?.stage && (
                                        <Badge variant="outline">
                                            {reportDoc.stage}
                                        </Badge>
                                    )}
                                </Group>

                                {typeof reportDoc?.progressPercent === "number" && (
                                    <Progress value={reportDoc.progressPercent} color={reportStatusColor} />
                                )}

                                <Text size="sm">
                                    {reportDoc?.message || "Waiting for Geneva to start processing the report."}
                                </Text>

                                {reportDoc?.result?.rowCount !== undefined && (
                                    <Text size="sm">
                                        Rows written: {reportDoc.result.rowCount}
                                    </Text>
                                )}

                                {resolvedReportId && reportDoc?.status === "completed" && (
                                    <Button
                                        size="xs"
                                        variant="light"
                                        leftSection={<IconPrinter size={14} />}
                                        onClick={() => window.open(`/report-print.html?reportId=${encodeURIComponent(resolvedReportId)}`, "_blank", "noopener,noreferrer")}
                                    >
                                        Open print view
                                    </Button>
                                )}

                                {(queuedReportId || reportDoc?.genevaRequestId) && (
                                    <Text size="xs" c="dimmed">
                                        Report ID: {queuedReportId || "Pending"}{reportDoc?.genevaRequestId ? ` | Geneva request: ${reportDoc.genevaRequestId}` : ""}
                                    </Text>
                                )}

                                {reportPath && (
                                    <Text size="xs" c="dimmed">
                                        Firestore path: {reportPath}
                                    </Text>
                                )}
                            </Stack>
                        </Alert>
                    )}

                    <Divider label="Previous reports" labelPosition="center" />

                    {recentReports.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            No previous {reportTitle} reports were found for this portfolio yet.
                        </Text>
                    ) : (
                        <Accordion chevronPosition="right" variant="separated">
                            {recentReports.map((item) => {
                                const itemStatusColor = getStatusColor(item.status);
                                return (
                                    <Accordion.Item key={item.id} value={item.id}>
                                        <Accordion.Control>
                                            <Group justify="space-between" wrap="nowrap">
                                                <Stack gap={0}>
                                                    <Text size="sm" fw={600}>
                                                        {formatReportRunDate(item.createdAt)}
                                                    </Text>
                                                    <Text size="xs" c="dimmed">
                                                        Report ID: {item.id}
                                                    </Text>
                                                </Stack>
                                                <Group gap="xs" wrap="nowrap">
                                                    {item.result?.rowCount !== undefined && (
                                                        <Badge variant="outline">
                                                            {item.result.rowCount} rows
                                                        </Badge>
                                                    )}
                                                    <Badge color={itemStatusColor} variant="filled">
                                                        {item.status || "queued"}
                                                    </Badge>
                                                </Group>
                                            </Group>
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                            <Stack gap="xs">
                                                {item.message && (
                                                    <Text size="sm">
                                                        {item.message}
                                                    </Text>
                                                )}

                                                {item.status === "completed" && (
                                                    <Button
                                                        size="xs"
                                                        variant="light"
                                                        leftSection={<IconPrinter size={14} />}
                                                        onClick={() => window.open(`/report-print.html?reportId=${encodeURIComponent(item.id)}`, "_blank", "noopener,noreferrer")}
                                                    >
                                                        Open print view
                                                    </Button>
                                                )}

                                                <Stack gap={4}>
                                                    {Object.entries(item.parameters || {}).map(([key, value]) => (
                                                        <Group key={key} gap="xs" align="flex-start" wrap="nowrap">
                                                            <Text size="xs" fw={700} w={150}>
                                                                {key}
                                                            </Text>
                                                            <Text size="xs" c="dimmed" style={{ wordBreak: "break-word" }}>
                                                                {value}
                                                            </Text>
                                                        </Group>
                                                    ))}
                                                </Stack>
                                            </Stack>
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                );
                            })}
                        </Accordion>
                    )}
                </Stack>
            </Modal>
        </>
    );
});

export default GenevaReportLauncherButton;
